/**
 * Menu ↔ Square name resolution.
 *
 * The restaurant's Square catalog item names differ from the website menu names,
 * so when we build an order we want to send the *Square* name (so the seller
 * dashboard reads correctly). This module fuzzy-matches a menu name to the
 * closest Square catalog name, and lets an admin pin exact overrides.
 *
 * Pure helpers (normalizeName, tokenize, bestMatch) are exported for testing;
 * useSquareNameMap is the React hook wiring catalog + Firestore overrides.
 */
import { useState, useEffect, useCallback } from 'react';
import { doc, onSnapshot, setDoc } from 'firebase/firestore';
import { db } from '../../../firebase';

const API_BASE = process.env.NODE_ENV === 'development'
  ? 'http://localhost:3001'
  : 'https://my-app-ramakrishna5s-projects.vercel.app';

// Strip size/qty/parenthetical noise and punctuation → comparable core.
export function normalizeName(s) {
  return (s || '')
    .toLowerCase()
    .replace(/\([^)]*\)/g, ' ')                         // (2 pc), (medium)
    .replace(/\b\d+\s*(oz|pc|pcs|pieces?|ct|pack)\b/g, ' ')
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9 ]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

export function tokenize(s) {
  const stop = new Set(['and', 'the', 'with', 'of', 'a', 'in', 'on']);
  return normalizeName(s).split(' ').filter(t => t && !stop.has(t));
}

function jaccard(aTokens, bTokens) {
  if (!aTokens.length || !bTokens.length) return 0;
  const a = new Set(aTokens), b = new Set(bTokens);
  let inter = 0;
  a.forEach(t => { if (b.has(t)) inter++; });
  return inter / (a.size + b.size - inter);
}

/**
 * Best Square name for a menu name.
 * @returns {{ name: string|null, score: number, exact: boolean }}
 */
export function bestMatch(menuName, squareNames) {
  const target = normalizeName(menuName);
  if (!target || !Array.isArray(squareNames) || squareNames.length === 0) {
    return { name: null, score: 0, exact: false };
  }
  const tTokens = tokenize(menuName);
  let best = { name: null, score: 0, exact: false };

  for (const sq of squareNames) {
    const nSq = normalizeName(sq);
    if (!nSq) continue;
    if (nSq === target) return { name: sq, score: 1, exact: true };

    let score = jaccard(tTokens, tokenize(sq));
    // containment bonus — one name fully inside the other is a strong signal
    if (nSq.includes(target) || target.includes(nSq)) {
      score = Math.max(score, 0.8 + Math.min(0.19, target.length / 200));
    }
    if (score > best.score) best = { name: sq, score, exact: false };
  }
  // require a reasonable match to avoid bad guesses
  if (best.score < 0.5) return { name: null, score: best.score, exact: false };
  return best;
}

// ─── Hook ────────────────────────────────────────────────────────────────────
/**
 * `enabled` = false skips both loads. Only the cart (canOrder) and the staff
 * mapping editor use this, but OrderingProvider mounts on every /menu visit —
 * so every browsing customer used to pay a Square catalog call plus a
 * squareItemMap listener that the rules (staff-only read) refused anyway.
 */
export function useSquareNameMap(enabled = true) {
  const [squareNames, setSquareNames] = useState([]);
  const [overrides, setOverrides] = useState({});   // { [menuName]: squareName }
  const [loading, setLoading] = useState(true);

  // 1. Square catalog names (flattened, deduped)
  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/square-catalog`);
        const json = await res.json();
        if (cancelled) return;
        const names = new Set();
        (json.catalog || []).forEach(cat => (cat.items || []).forEach(n => names.add(n)));
        setSquareNames([...names].sort());
      } catch {
        if (!cancelled) setSquareNames([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [enabled]);

  // 2. Admin overrides (live)
  useEffect(() => {
    if (!enabled) return undefined;
    const unsub = onSnapshot(doc(db, 'squareItemMap', 'main'),
      snap => setOverrides(snap.exists() ? (snap.data().map || {}) : {}),
      () => setOverrides({}));
    return () => unsub();
  }, [enabled]);

  /** Resolve a menu name → { squareName, source, score }. */
  const resolve = useCallback((menuName) => {
    if (overrides[menuName]) return { squareName: overrides[menuName], source: 'override', score: 1 };
    const m = bestMatch(menuName, squareNames);
    if (m.name) return { squareName: m.name, source: 'auto', score: m.score };
    return { squareName: menuName, source: 'none', score: 0 };
  }, [overrides, squareNames]);

  const setOverride = useCallback(async (menuName, squareName) => {
    const next = { ...overrides };
    if (squareName) next[menuName] = squareName; else delete next[menuName];
    setOverrides(next); // optimistic
    await setDoc(doc(db, 'squareItemMap', 'main'), { map: next }, { merge: true }).catch(() => {});
  }, [overrides]);

  return { squareNames, overrides, resolve, setOverride, loading };
}
